/**
 * Utilitário de captura, persistência e propagação de parâmetros UTM
 * Extrai UTMs da URL pública, persiste em sessionStorage e propaga para dataLayer
 *
 * @release v1.0.0 - 2026-03-08
 * @description Etapa 4 do sistema de rastreamento — captura automática de UTMs
 */

import { logger } from '@/lib/logger';

/** Chave do sessionStorage para persistência dos UTMs */
const STORAGE_KEY = 'uaizychat_utm';

/**
 * Interface tipada dos parâmetros UTM e identificadores de clique
 */
export interface UTMParams {
  /** Origem do tráfego (ex: google, facebook, newsletter) */
  utm_source?: string;
  /** Meio de marketing (ex: cpc, email, social) */
  utm_medium?: string;
  /** Nome da campanha (ex: black_friday_2026) */
  utm_campaign?: string;
  /** Termo de busca pago (ex: crm whatsapp) */
  utm_term?: string;
  /** Variação do conteúdo (ex: banner_v2) */
  utm_content?: string;
  /** Google Click ID — capturado automaticamente pelo Google Ads */
  gclid?: string;
  /** Facebook Click ID — capturado automaticamente pelo Meta Ads */
  fbclid?: string;
  /** Referência customizada (parâmetro ?ref=) */
  ref?: string;
}

/** Lista de parâmetros capturados da URL */
const UTM_KEYS: (keyof UTMParams)[] = [
  'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content',
  'gclid', 'fbclid', 'ref',
];

/**
 * Captura parâmetros UTM da URL atual e persiste em sessionStorage.
 * Idempotente: se já existirem dados no storage, não sobrescreve
 * (primeira visita ganha — preserva atribuição original).
 *
 * @returns Objeto UTMParams com os valores capturados
 */
export function captureUTMParams(): UTMParams {
  try {
    // Verifica se já capturou nesta sessão (first-touch attribution)
    const existing = sessionStorage.getItem(STORAGE_KEY);
    if (existing) {
      logger.debug('utmCapture', 'UTMs já capturados nesta sessão, reutilizando');
      return JSON.parse(existing) as UTMParams;
    }

    // Extrai parâmetros da URL
    const searchParams = new URLSearchParams(window.location.search);
    const params: UTMParams = {};
    let hasAny = false;

    for (const key of UTM_KEYS) {
      const value = searchParams.get(key);
      if (value) {
        params[key] = decodeURIComponent(value);
        hasAny = true;
      }
    }

    // Captura referrer do navegador como fallback de source
    if (!params.utm_source && document.referrer) {
      try {
        const referrerHost = new URL(document.referrer).hostname;
        // Só usa referrer se não for o próprio domínio
        if (referrerHost && referrerHost !== window.location.hostname) {
          params.ref = params.ref || referrerHost;
        }
      } catch {
        // Referrer inválido, ignora
      }
    }

    // Persiste no sessionStorage
    if (hasAny || params.ref) {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(params));
      logger.info('utmCapture', `UTMs capturados: ${JSON.stringify(params)}`);
    } else {
      // Persiste objeto vazio para marcar que já tentou capturar
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(params));
      logger.debug('utmCapture', 'Nenhum parâmetro UTM encontrado na URL');
    }

    return params;
  } catch (error) {
    logger.error('utmCapture', 'Erro ao capturar UTMs', error);
    return {};
  }
}

/**
 * Recupera parâmetros UTM previamente capturados do sessionStorage.
 * Útil para acessar UTMs em qualquer ponto da sessão sem depender da URL.
 *
 * @returns Objeto UTMParams ou vazio se não houver dados
 */
export function getUTMParams(): UTMParams {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored) as UTMParams;
    }
  } catch (error) {
    logger.error('utmCapture', 'Erro ao ler UTMs do sessionStorage', error);
  }
  return {};
}

/**
 * Envia parâmetros UTM capturados para o dataLayer do GTM.
 * Dispara evento 'utm_captured' para que triggers no GTM possam
 * reagir à presença de UTMs (ex: criar variáveis de dataLayer).
 *
 * Idempotente: verifica se já foi enviado nesta sessão.
 */
export function pushUTMToDataLayer(): void {
  try {
    const params = getUTMParams();

    // Só envia se houver pelo menos 1 parâmetro preenchido
    const hasValues = Object.values(params).some((v) => v);
    if (!hasValues) {
      return;
    }

    // Verifica se dataLayer existe (GTM pode não estar configurado)
    if (!window.dataLayer) {
      logger.debug('utmCapture', 'dataLayer não disponível, pulando push de UTMs');
      return;
    }

    // Envia evento com todos os UTMs
    window.dataLayer.push({
      event: 'utm_captured',
      ...params,
    });

    logger.info('utmCapture', 'UTMs enviados ao dataLayer');
  } catch (error) {
    logger.error('utmCapture', 'Erro ao enviar UTMs ao dataLayer', error);
  }
}
